// Import the RTK Query methods from the React-specific entry point
import { BaseQueryFn, createApi } from "@reduxjs/toolkit/query/react";
import { Auth } from "aws-amplify";
import axios, {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
} from "axios";
import {
  ActivateAssetRequest,
  CountriesProps,
  EmploymentTitlesProps,
  GroupProps,
  GroupsResponse,
  RelatedAssetsResponse,
  ResponseUserGroup,
  ShareAssetRequest,
  SubmitFeedbackRequest,
  SuggestedUserResponse,
  TrackingRequest,
  ManagedUser,
  EditUserRequest,
  AwsPolicyProps,
  UserRoleResponse,
  UserPermissionsResponse,
  AssetEditDto,
  AssetCreateDto,
  ExpireAssetRequest,
  FileDownloadRequest,
  CategoryResponse,
  DownloadedAssetsQueryResponse,
  Asset,
  GroupUsersRequest,
  UserProfile,
  FunctionalArea,
  Channel,
  SavedSearchResponse,
  ShareSavedSearchRequest,
  FileCourierSentPackageDetailsResponse,
  DeleteFileCourierFileRequest,
  DeleteFileCourierPackageRequest,
  ExtendFileCourierPackageRequest,
  GetFileCourierPackageDetailsRequest,
  ResponseRequestList,
  AccessRequestDetails,
  DeclineAssetAccess,
  EmailTemplateMetadata,
  EmailTemplate,
  EmailPreviewResponse,
  FileCourierPackagesResponse,
  FileCourierSearchProps,
  RekognitionResponse,
  AdminAssetSearchResponse,
  RetailGovernancePage,
  RetailSupportPage,
  HomePage,
  TemplatesPage,
  ContactUsProps,
  TrainingOverviewPageProps,
  TrainingVideosPageProps,
  ComplianceOverviewPageProps,
  ComplianceProcessPageProps,
  ComplianceScoringPageProps,
  ComplianceResourcePageProps,
  ComplianceFaqsPageProps,
  BrandUpdatePage,
  RetailDesignGuidePageProps,
  RetailPageProps,
  RetailDesignAdvisoriesPageProps,
  RetailTemplatesPageProps,
  RetailGovernanceReportsPageProps,
  RetailContactUsPageProps,
  RetailToolsPageProps,
  DistributionList,
  ContactForm,
  GlobalDataProps,
  FavoriteFolderProps,
  UnifiedSearchResultProps,
  FavoritesPageFolderProps,
  UnifiedSearchResponse,
  UnifiedCardsResult,
  ProjectCreateInstanceForm,
  ProjectUpdateInstanceForm,
  ProjectInstanceResponse,
  ProjectTeamProps,
  ProjectTeamsUpdateRequest,
  ProjectGroupProps,
  ProjectTeamCreateRequest,
  PackagingGroupsResponse,
  GetProjectTasksForm,
  ProjectTask,
  GetProjectTaskForm,
  ProjectTaskUpdateRequest,
  TaskFormType,
  TaskCreateRequest,
  DeleteTaskRequest,
  ProjectsSearchForm,
  PackagingManagerProjectSearchResults,
  FileCreateRequest,
  DeleteTaskFileRequest,
  DeleteProjectFileRequest,
  DeleteFormFileRequest,
  DeleteFileVersionRequest,
  FileVersionsRequest, FileVersionResult, FormDetailsRequest, FormInstanceVersion, SubmitFormRequest, FilesSearchForm, MultipleFileCreateRequest, MultipleFileUpdateRequest, FormFileUpdateCreateResponse, FileCourierSentPackageProps,
} from "interfaces";
import {
  ActiveCategory,
  ActiveSubCategory,
  ActiveTag,
  AssetDashboard,
} from "interfaces/Asset";
import { setNotification } from "services/ui/ui.slice";
import { API_URL } from "utils/environmentVariables";
import {CreateFavoriteItemRequest} from "../../interfaces/Favorites";
import {SuggestedSearchResponse} from "../../interfaces/Search";
import {ProjectMessagesCreateRequest, MessagesSearchForm, PackagingManagerFileSearchResults, PackagingManagerFormResponse, PackagingManagerMessageSearchResults, ProjectBriefSearchResponse, ProjectTaskDetailsResponse, ProjectTypeProps, UpdateFormRequest, ProjectMessageDeleteRequest, ProjectMessageUpdateRequest, ProjectTeamDeleteRequest, ProjectFoldersResponse, ProjectFolderRequestForm, ProjectFolderProps, ProjectFoldersUpdateRequest, GroupMemberProps, ProjectGroupMembersRequest, FileUpdateFolderRequest} from "../../interfaces/PackagingManager";
import { CreateDefaultTasksRequest, EditWorkflowPermissionsRequest, GroupType, WorkflowPermissionDto } from "interfaces/AdminManagement";
import {FileCourierPackagePostProps} from "../../interfaces/FileCourier";

export const axiosBaseQuery =
  (): BaseQueryFn<{
    url: string;
    method?: AxiosRequestConfig["method"];
    body?: AxiosRequestConfig["data"];
    params?: AxiosRequestConfig["params"];
    headers?: AxiosRequestConfig["headers"];
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    auth?: boolean;
  }> =>
  async (
    {
      url,
      method = "get",
      body = {},
      params,
      headers = {
        accepts: "application/json",
        "Content-type": "application/json; charset=UTF-8",
      },
      onUploadProgress = () => {},
      auth = true,
    },
    api
  ) => {
    try {
      if (auth) {
        const accessToken = (await Auth.currentSession())
          .getIdToken()
          .getJwtToken();
        headers.Authorization = `Bearer ${accessToken}`;
      }
      const result = await axios({
        url: API_URL + url,
        method,
        data: body,
        params,
        headers,
        onUploadProgress:
          process.env.NODE_ENV === "test" ? undefined : onUploadProgress,
      });
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError as AxiosError;
      api.dispatch(
        setNotification({
          type: "error",
          message:
            "Some data may not have loaded correctly. Please refresh to try again.",
        })
      );
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

// Define our single API slice object
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: "api",
  // All of our requests will have URLs starting with '/api/v1.0'
  baseQuery: axiosBaseQuery(),

  // tags to invalidate data
  tagTypes: ["Fetch"],
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    getAwsPolicy: builder.query<AwsPolicyProps, void>({
      query: () => ({ url: "/Aws/policy" }),
    }),
    getAwsVideoPolicy: builder.query<AwsPolicyProps, void>({
      query: () => ({ url: "/Aws/video-policy" }),
    }),
    getCountries: builder.query<CountriesProps, void>({
      query: () => ({ url: "/countries", auth: false }),
    }),
    getEmploymentTitles: builder.query<EmploymentTitlesProps, void>({
      query: () => ({ url: "/employmentTitles", auth: false }),
    }),
    getGlobal: builder.query<GlobalDataProps, void>({
      query: () => ({ url: "/cms/global" }),
    }),
    getAccountTypes: builder.query<Array<{ name: string }>, void>({
      query: () => ({ url: "/admin/users/accountTypes" }),
      transformResponse: (response: Array<string>) => {
        return response.map((item) => {
          return { name: item };
        });
      },
    }),
    getRoles: builder.query<Array<{ value: string; label: string }>, void>({
      query: () => ({ url: "/admin/users/roles" }),
      transformResponse: (response: Array<UserRoleResponse>) => {
        return response.map((item) => {
          return { value: item.id, label: item.description };
        });
      },
    }),
    getPermissions: builder.query<
      Array<{ value: string; label: string; parentId: string | null }>,
      void
    >({
      query: () => ({ url: "/admin/users/permissions" }),
      transformResponse: (response: Array<UserPermissionsResponse>) => {
        return response.map((item) => {
          return {
            value: item.id,
            label: item.description,
            parentId: item.parentId,
          };
        });
      },
    }),
    addLogInToHistory: builder.mutation<boolean, void>({
      query: () => {
        return {
          url: `/users/history/login`,
          method: "PUT",
        };
      },
    }),
    getFavoriteItems: builder.query<Array<UnifiedSearchResultProps>, any>({
      query: (id) => ({ url: `/favorites/${id}` }),
      providesTags: () => [
        { type: "Fetch", id: "FAVORITEITEMS" },
      ],
    }),
    getFavoriteFolders: builder.query<Array<FavoriteFolderProps>, void>({
      query: () => ({ url: "/favorites/folders" }),
      providesTags: () => [
        { type: "Fetch", id: "FOLDERS" },
      ],
    }),
    getFavoritesPageFolders: builder.query<Array<FavoritesPageFolderProps>, void>({
      query: () => ({ url: "/favorites/favorites-page-folders" }),
      providesTags: () => [
        { type: "Fetch", id: "FAVORITESFOLDERS" },
      ],
    }),
    addFavoriteAsset: builder.mutation<Array<string>, CreateFavoriteItemRequest>({
      query: (payload) => ({
        url: "/favorites",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Fetch", id: "FAVORITEITEMS" }],
    }),
    removeFavoriteAsset: builder.mutation<any, string>({
      query: (id) => ({
        url: `/favorites?id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Fetch", id: "FAVORITEITEMS" }],
    }),
    deleteFavoriteFolder: builder.mutation<void, string>({
      query: (folderId) => ({
        url: `/favorites/delete-folder?id=${folderId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Fetch", id: "FAVORITESFOLDERS" }],
    }),
    activateAsset: builder.mutation<any, any>({
      query: (activateAssetRequest: ActivateAssetRequest) => ({
        url: `/asset/${activateAssetRequest.assetId}/is-active/${activateAssetRequest.isActive}`,
        method: "PUT",
        body: activateAssetRequest.payload,
      }),
    }),
    expireAsset: builder.mutation<any, any>({
      query: (expireAssetRequest: ExpireAssetRequest) => {
        return {
          url: `/asset/${expireAssetRequest.assetId}/expires-on/${expireAssetRequest.expirationDate}`,
          method: "PUT",
        };
      },
    }),
    trackFeedback: builder.mutation<any, any>({
      query: (submitFeedbackRequest: SubmitFeedbackRequest) => ({
        url: `/tracking/feedback`,
        method: "POST",
        body: JSON.stringify({
          correlationId: submitFeedbackRequest.correlationId,
          source: submitFeedbackRequest.source,
          response: submitFeedbackRequest.response,
          details: submitFeedbackRequest.details,
          entityId: submitFeedbackRequest.entityId,
        }),
      }),
    }),
    tracking: builder.mutation<any, TrackingRequest>({
      query: (trackingRequest: TrackingRequest) => ({
        url: `/tracking`,
        method: "POST",
        body: JSON.stringify(trackingRequest),
      }),
    }),
    acceptUsageRights: builder.mutation<any, string>({
      query: (assetId) => ({
        url: `/asset/${assetId}/usage-rights/accept`,
        method: "PUT",
      }),
    }),
    shareAssets: builder.mutation<any, ShareAssetRequest>({
      query: (shareAssetRequest: ShareAssetRequest) => ({
        url: `/asset/share`,
        method: "POST",
        body: JSON.stringify({
          to: shareAssetRequest.recipientIds,
          shareItems: shareAssetRequest.shareItems,
          message: shareAssetRequest.message,
        }),
      }),
    }),
    getFeedbackOptions: builder.query<string[], void>({
      query: () => ({ url: `/tracking/feedback/reasons` }),
    }),
    getFilters: builder.query<CategoryResponse, void>({
      query: () => ({
        url: `/filters/all`,
      }),
    }),
    getCategories: builder.query<ActiveCategory[], void>({
      query: () => ({
        url: `/filters/categories`,
      }),
    }),
    newCategory: builder.mutation<{ id: string }, string>({
      query: (value) => ({
        url: `/filters/categories`,
        method: "POST",
        body: { active: true, value },
      }),
    }),
    editCategory: builder.mutation<
      any,
      { categoryId: string; payload: { active: boolean; value: string } }
    >({
      query: ({ categoryId, payload }) => ({
        url: `/filters/categories/${categoryId}`,
        method: "PUT",
        body: payload,
      }),
    }),
    getSubCategories: builder.query<ActiveSubCategory[], string>({
      query: (categoryId) => ({
        url: `/filters/subcategories?CategoryId=${categoryId}`,
      }),
    }),
    newSubCategory: builder.mutation<
      { id: string },
      { categoryId: string; value: string }
    >({
      query: (payload) => ({
        url: `/filters/subcategories`,
        method: "POST",
        body: { active: true, ...payload },
      }),
    }),
    editSubCategory: builder.mutation<
      any,
      { subCategoryId: string; payload: { active: boolean; value: string } }
    >({
      query: ({ subCategoryId, payload }) => ({
        url: `/filters/subcategories/${subCategoryId}`,
        method: "PUT",
        body: payload,
      }),
    }),
    getTags: builder.query<ActiveTag[], string>({
      query: (subCategoryId) => ({
        url: `/filters/tags?SubcategoryId=${subCategoryId}`,
      }),
    }),
    newTag: builder.mutation<
      { id: string },
      { subcategoryId: string; value: string }
    >({
      query: (payload) => ({
        url: `/filters/tags`,
        method: "POST",
        body: { active: true, ...payload },
      }),
    }),
    editTag: builder.mutation<
      any,
      { tagId: string; payload: { active: boolean; value: string } }
    >({
      query: ({ tagId, payload }) => ({
        url: `/filters/tags/${tagId}`,
        method: "PUT",
        body: payload,
      }),
      // invalidatesTags: [{ type: "Fetch", id: "EDIT_TAGS" }],
    }),
    getAssetsDashboard: builder.query<AssetDashboard, void>({
      query: () => ({
        url: `/cms/asset-dashboard`,
      }),
    }),
    getNewAssets: builder.query<UnifiedSearchResultProps[], void>({
      query: () => ({
        url: `/assets/new`,
      }),
      transformResponse: (response: UnifiedCardsResult) => {
        return response.results;
      },
    }),
    getRecommendedAssets: builder.query<UnifiedSearchResultProps[], void>({
      query: () => ({
        url: `/assets/recommended`,
      }),
      transformResponse: (response: UnifiedCardsResult) => {
        return response.results;
      },
    }),
    getSimilarAssets: builder.query<
      { resultSet: string; results: UnifiedSearchResultProps[] },
      void
    >({
      query: () => ({
        url: `/assets/downloaded/similar`,
      }),
      transformResponse: (response: UnifiedCardsResult) => {
        return { resultSet: response.resultSet, results: response.results };
      },
    }),
    getAssignedAssets: builder.query<UnifiedSearchResultProps[], void>({
      query: () => ({
        url: `/assets/assigned`,
      }),
    }),
    getAssetDownloadUrl: builder.query<string, string>({
      query: (assetId) => ({
        url: `/assets/${assetId}/download`,
      }),
    }),
    getFileDownloadUrl: builder.query<string, FileDownloadRequest>({
      query: (fileDownloadRequest) => ({
        url: `/assets/${fileDownloadRequest.assetId}/download/${fileDownloadRequest.fileVersionId}`,
      }),
    }),
    getFileGroupDownloadUrl: builder.query<string, FileDownloadRequest>({
      query: (fileDownloadRequest) => ({
        url: `/assets/${fileDownloadRequest.assetId}/download/${fileDownloadRequest.fileVersionId}/group`,
      }),
    }),
    getChannelsUses: builder.query<any, void>({
      query: () => ({ url: "/assets/access-requests/channels-uses" }),
    }),
    accessRequests: builder.mutation<any, { assetId: string; payload: any }>({
      query: ({ assetId, payload }) => ({
        url: `/assets/access-requests/${assetId}`,
        method: "POST",
        body: payload,
      }),
    }),
    adminGetUsers: builder.query<ResponseUserGroup, string>({
      query: (query) => ({
        url: `/admin/users${query}`,
      }),
    }),
    adminGetUser: builder.query<ManagedUser, { userId: string }>({
      query: ({ userId }) => {
        return {
          url: `/admin/users/${userId}`,
        };
      },
      // providesTags: () => [{ type: "Fetch", id: "ADMIN_USER" }],
    }),
    adminAssetSearch: builder.query<AdminAssetSearchResponse, string>({
      query: (query) => ({
        url: `/asset/adminsearch${query}`,
      }),
    }),
    getUserProfile: builder.query<UserProfile, void>({
      query: () => ({
        url: `/users`,
      }),
      providesTags: () => [{ type: "Fetch", id: "USER_PROFILE" }],
    }),
    getFunctionalAreas: builder.query<FunctionalArea[], void>({
      query: () => ({
        url: `/functional-areas`,
      }),
    }),
    getChannels: builder.query<Channel[], void>({
      query: () => ({
        url: `/channels`,
      }),
    }),
    updateUserProfile: builder.mutation<UserProfile, UserProfile>({
      query: (data) => ({
        url: `/users`,
        method: "PUT",
        body: JSON.stringify(data),
      }),
      invalidatesTags: [{ type: "Fetch", id: "USER_PROFILE" }],
    }),
    deleteUser: builder.mutation<ManagedUser, EditUserRequest>({
      query: (deleteUserRequest: EditUserRequest) => ({
        url: `/admin/users/${deleteUserRequest.id}/delete`,
        method: "PUT",
        body: JSON.stringify(deleteUserRequest.userData),
      }),
      // invalidatesTags: [{ type: "Fetch", id: "GROUPS" }],
    }),
    approveUser: builder.mutation<ManagedUser, EditUserRequest>({
      query: (approveUserRequest: EditUserRequest) => ({
        url: `/admin/users/${approveUserRequest.id}/approve`,
        method: "PUT",
        body: JSON.stringify(approveUserRequest.userData),
      }),
    }),
    updateUser: builder.mutation<ManagedUser, EditUserRequest>({
      query: (updateUserRequest: EditUserRequest) => ({
        url: `/admin/users/${updateUserRequest.id}`,
        method: "PUT",
        body: JSON.stringify(updateUserRequest.userData),
      }),
      // invalidatesTags: [{ type: "Fetch", id: "ADMIN_USER" }],
    }),
    getSuggestedUsers: builder.query<SuggestedUserResponse, string>({
      query: (query) => ({
        url: `/user/suggest?q=${query}`,
      }),
    }),
    getRelatedAssets: builder.query<
      Array<{ id: string; name: string }>,
      string
    >({
      query: (query) => ({
        url: `/unified/search/lightweight?pagesize=50&page=1&includerestricted=true&q=${query}`,
      }),
      transformResponse: (response: RelatedAssetsResponse) => {
        return response.results;
      },
    }),
    getUploadedAssets: builder.query<DownloadedAssetsQueryResponse, string>({
      query: (uploadQuery: string) => ({
        url: `/assets/uploaded${uploadQuery}`,
      }),
    }),
    getDownloadedAssets: builder.query<DownloadedAssetsQueryResponse, string>({
      query: (downloadQuery: string) => ({
        url: `/assets/downloaded${downloadQuery}`,
      }),
    }),
    getGroups: builder.query<Array<GroupProps>, void>({
      query: () => ({
        url: `/users/groups?Status=Active`,
      }),
      transformResponse: (response: GroupsResponse) => {
        return response.data.map((group) => {
          return {
            id: group.Id,
            name: group.Name,
            company: group.Company,
            active: group.Active,
          };
        });
      },
    }),
    getPackagingManagerGroups: builder.query<Array<ProjectGroupProps>, void>({
      query: () => ({
        url: `/users/groups?ClientCode=PKM`,
      }),
      transformResponse: (response: PackagingGroupsResponse) => {
        return response.data.map((group) => {
          return {
            id: group.Id,
            name: group.Name,
            company: group.Company,
            groupType: group.groupType ? {
              id: group.groupType?.id,
              name: group.groupType?.name,
              description: group.groupType?.description
            } : undefined,
            status: group.Status,
            isWorkflowGroup: group.isWorkflowGroup
          };
        });
      },
    }),
    adminGetGroups: builder.query<ResponseUserGroup, string>({
      query: (query) => ({
        url: `/users/groups${query}`,
      }),
      providesTags: () => [{ type: "Fetch", id: "ADMIN_GROUPS" }],
    }),
    adminGetGroup: builder.query<ManagedUser, { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/groups/${userId}`,
      }),
      providesTags: () => [{ type: "Fetch", id: "ADMIN_GROUP" }],
    }),
    adminCreateGroup: builder.mutation<ManagedUser, ManagedUser>({
      query: (createGroupRequest: ManagedUser) => ({
        url: `/users/groups`,
        method: "POST",
        body: JSON.stringify(createGroupRequest),
      }),
    }),
    adminUpdateGroup: builder.mutation<ManagedUser, EditUserRequest>({
      query: (editGroupRequest: EditUserRequest) => ({
        url: `/users/groups/${editGroupRequest.id}`,
        method: "PUT",
        body: JSON.stringify(editGroupRequest.userData),
      }),
      invalidatesTags: [
        { type: "Fetch", id: "GROUPS" },
        { type: "Fetch", id: "ADMIN_GROUPS" },
        { type: "Fetch", id: "ADMIN_GROUP" },
      ],
    }),
    getGroupMembers: builder.query<ResponseUserGroup, GroupUsersRequest>({
      query: (groupUsersRequest) => ({
        url: `/users/groups/${groupUsersRequest.id}/members${groupUsersRequest.query}`,
      }),
      providesTags: () => [{ type: "Fetch", id: "GROUPS" }],
    }),
    getThumbnails: builder.query<any, { id: string; ext?: string | null }>({
      query: ({ id, ext }) => ({
        url: `/asset/files/versions/${id}/thumbnails/${
          ext ? ext.replace(".", "").toLowerCase() : "png"
        }`,
      }),
    }),
    getAssets: builder.query<Asset[], string>({
      query: (assetIds) => ({
        url: `/asset${assetIds}`,
      }),
      providesTags: () => [{ type: "Fetch", id: "ASSET_EDIT" }],
    }),
    getAssetDetails: builder.query<Asset, string>({
      query: (assetId) => ({
        url: `/asset${assetId}`,
      }),
      providesTags: () => [{ type: "Fetch", id: "ASSET_DETAILS" }],
    }),
    editAsset: builder.mutation<
      ManagedUser,
      { assetId?: string; payload: AssetEditDto }
    >({
      query: ({ assetId, payload }) => ({
        url: `/asset/${assetId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [{ type: "Fetch", id: "ASSET_EDIT" }],
    }),
    createAsset: builder.mutation<void, { payload: AssetCreateDto }>({
      query: ({ payload }) => ({
        url: `/asset`,
        method: "POST",
        body: JSON.stringify(payload),
      }),
    }),
    getRelatedAssetsSpecific: builder.query<UnifiedSearchResultProps[], string>({
      query: (assetId) => ({
        url: `/asset/${assetId}/related`,
      }),
      transformResponse: (response: UnifiedCardsResult) => {
        return response.results;
      },
    }),
    getSimilarAssetsSpecific: builder.query<UnifiedSearchResultProps[], string>({
      query: (assetId) => ({
        url: `/asset/${assetId}/similar`,
      }),
      transformResponse: (response: UnifiedCardsResult) => {
        return response.results;
      },
    }),
    approveFile: builder.mutation<
      ManagedUser,
      {
        assetId?: string;
        payload: Array<{ fileVersionId: string; approvalStatus: string }>;
      }
    >({
      query: ({ assetId, payload }) => ({
        url: `/asset/${assetId}/assign/file-approval`,
        method: "PUT",
        body: payload,
      }),
    }),
    searchAssets: builder.query<UnifiedSearchResponse, any>({
      query: ({
        search,
        searchTypes,
        pageNumber = 1,
        pageSize,
        sortBy = "relevance",
        updateSearchHistory,
        correlationId,
        includeRestricted = true,
        includeExpired = false,
        expiresOn = null,
        categoryId,
        selectedTags = [],
      }) => {
        let params = new URLSearchParams();
        const values = {
          searchTypes: searchTypes,
          page: pageNumber,
          pageSize: pageSize,
          ...(categoryId ? { andfilters: categoryId } : {}),
          sort: sortBy,
          ...(search ? { q: search } : {}),
          correlationid: correlationId,
          includerestricted: includeRestricted,
          includeexpired: includeExpired,
          ...(expiresOn ? { excludewithinexpireddate: expiresOn } : {}),
          updateSearchHistory: !!search && updateSearchHistory ? true : false,
        };
        Object.entries(values)
          .filter(
            ([key, value]: [string, any]) =>
              value !== null && value !== undefined
          )
          .map(([key, value]: [string, any]) => {params.append(key, value);});
        selectedTags.map((tag: any) => {params.append("andfilters", tag.id); });

        return {
          url: `/unified/search`,
          params: params,
        };
      },
    }),
    searchGuidelines: builder.query<any, any>({
      query: ({ pageNumber, pageSize, search, updateSearchHistory }) => {
        return {
          url: `/guidelines/search`,
          params: {
            page: pageNumber,
            pageSize: pageSize,
            q: search,
            updateSearchHistory: !!search && updateSearchHistory ? true : false,
          },
        };
      },
    }),
    searchTraining: builder.query<any, any>({
      query: ({ pageNumber, pageSize, search, updateSearchHistory }) => {
        return {
          url: `/cms/training-videos/search`,
          params: {
            page: pageNumber,
            pageSize: pageSize,
            q: search,
            updateSearchHistory: !!search && updateSearchHistory ? true : false,
          },
        };
      },
    }),
    searchCompliance: builder.query<any, any>({
      query: ({ pageNumber, pageSize, search, updateSearchHistory }) => {
        return {
          url: `/cms/compliance/search`,
          params: {
            page: pageNumber,
            pageSize: pageSize,
            q: search,
            updateSearchHistory: !!search && updateSearchHistory ? true : false,
          },
        };
      },
    }),
    requestAsset: builder.mutation<void, { payload: any }>({
      query: ({ payload }) => ({
        url: `/forms/request-asset`,
        method: "POST",
        body: payload,
      }),
    }),
    getSuggestedSearch: builder.query<SuggestedSearchResponse, any>({
      query: ({ search, correlationId }) => ({
        url: "/unified-search/did-you-mean?page=1&pageSize=5",
        params: {
          q: search,
          correlationId
        },
      }),
    }),
    getRecentSearches: builder.query<any, void>({
      query: () => ({
        url: "/search/recent",
      }),
      providesTags: () => [{ type: "Fetch", id: "RECENT_SEARCHES" }],
    }),
    getRelatedSearches: builder.query<any, any>({
      query: ({ search }) => ({
        url: `/unified-search/autocomplete?q=${search}`,
      }),
    }),
    deleteRecentSearch: builder.mutation<
      any,
      {
        searchId?: string;
      }
    >({
      query: ({ searchId }) => ({
        url: `/search/recent/${searchId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Fetch", id: "RECENT_SEARCHES" }],
    }),
    getSavedSearches: builder.query<SavedSearchResponse[], void>({
      query: () => ({ url: `/favorites/savedSearches` }),
      providesTags: () => [{ type: "Fetch", id: "SAVED_SEARCHES" }],
    }),
    getSavedSearch: builder.query<SavedSearchResponse, string>({
      query: (searchId) => ({ url: `/favorites/savedSearches/${searchId}` }),
    }),
    saveSearch: builder.mutation<
      void,
      {
        Name: string;
        SearchObject: string;
        UserId: string;
        SearchType: string;
      }
    >({
      query: ({ Name, SearchObject, UserId, SearchType }) => ({
        url: `/favorites/savedSearches`,
        method: "POST",
        body: { Name, SearchObject, UserId, SearchType },
      }),
      invalidatesTags: [{ type: "Fetch", id: "SAVED_SEARCHES" }],
    }),
    sendFeedback: builder.mutation<any, any>({
      query: (payload) => ({
        url: "/feedback",
        method: "POST",
        body: payload,
      }),
    }),
    sendContact: builder.mutation<any, ContactForm>({
      query: (contactForm: ContactForm) => ({
        url: `/distribution-lists/${contactForm.distributionId}/notify`,
        method: "POST",
        body: JSON.stringify({
          name: contactForm.name,
          message: contactForm.comments,
        }),
      }),
    }),
    shareSavedSearch: builder.mutation<void, ShareSavedSearchRequest>({
      query: (shareSavedSearchRequest: ShareSavedSearchRequest) => ({
        url: `/favorites/savedSearches/share`,
        method: "POST",
        body: JSON.stringify({
          to: shareSavedSearchRequest.recipientIds,
          searchIds: shareSavedSearchRequest.searchIds,
          message: shareSavedSearchRequest.message,
        }),
      }),
    }),
    deleteSavedSearch: builder.mutation<void, string>({
      query: (searchId) => ({
        url: `/favorites/savedSearches/${searchId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Fetch", id: "SAVED_SEARCHES" }],
    }),
    renameSavedSearch: builder.mutation<void, SavedSearchResponse>({
      query: (renameSavedSearchRequest: SavedSearchResponse) => ({
        url: `/favorites/savedSearches/${renameSavedSearchRequest.id}`,
        method: "PUT",
        body: JSON.stringify({
          Name: renameSavedSearchRequest.name,
          SearchObject: renameSavedSearchRequest.searchObject,
        }),
      }),
      invalidatesTags: [{ type: "Fetch", id: "SAVED_SEARCHES" }],
    }),
    getRequestorAccessRequests: builder.query<ResponseRequestList, string>({
      query: (query) => ({ url: `/assets/access-requests/requestor${query}` }),
      providesTags: () => [{ type: "Fetch", id: "GET_MY_REQUESTS" }],
    }),
    getApproverAccessRequests: builder.query<ResponseRequestList, string>({
      query: (query) => ({ url: `/assets/access-requests/approver${query}` }),
      providesTags: () => [{ type: "Fetch", id: "GET_MANAGED_REQUESTS" }],
    }),
    getDownloadAccessRequestsLink: builder.query<void, void>({
      query: () => ({ url: "/assets/access-requests/export"})
    }),
    getAwsFileCourierPolicy: builder.query<AwsPolicyProps, void>({
      query: () => ({ url: "/Aws/file-courier/temp" }),
    }),
    sendFileCourierPackage: builder.mutation<boolean, FileCourierPackagePostProps>({
      query: (payload: FileCourierPackagePostProps) => ({
        url: "/file-courier/package/send",
        method: "POST",
        body: payload
      }),
    }),
    deleteFileCourierFile: builder.mutation<
      boolean,
      DeleteFileCourierFileRequest
    >({
      query: ({ packageId, fileName }: DeleteFileCourierFileRequest) => ({
        url: "/file-courier/file/delete/temp",
        method: "DELETE",
        params: {
          packageId,
          fileName,
        },
      }),
    }),
    getFileCourierSentPackages: builder.query<
      Array<FileCourierSentPackageProps>,
      FileCourierSearchProps
    >({
      query: ({
        orderBy,
        orderDirection,
        page,
        pageCount,
      }: FileCourierSearchProps) => ({
        url: `/file-courier/packages/sent`,
        params: {
          orderBy,
          orderDirection,
          page,
          pageCount,
        },
      }),
    }),
    getFileCourierReceivedPackages: builder.query<
      FileCourierPackagesResponse,
      FileCourierSearchProps
    >({
      query: ({
        orderBy,
        orderDirection,
        page,
        pageCount,
      }: FileCourierSearchProps) => ({
        url: `/file-courier/packages/received`,
        params: {
          orderBy,
          orderDirection,
          page,
          pageCount,
        },
      }),
    }),
    deleteFileCourierPackage: builder.mutation<
      string,
      DeleteFileCourierPackageRequest
    >({
      query: ({ packageId }: DeleteFileCourierPackageRequest) => ({
        url: `/file-courier/package/delete/${packageId}`,
        method: "DELETE",
      }),
    }),
    extendFileCourierPackage: builder.mutation<
      string,
      ExtendFileCourierPackageRequest
    >({
      query: ({ packageId, newExpiration }: ExtendFileCourierPackageRequest) => ({
        url: `/file-courier/package/extend/${packageId}`,
        params: {
          newExpiration
        },
        method: "PUT"
      }),
    }),
    getSentFileCourierPackageDetails: builder.query<
      FileCourierSentPackageDetailsResponse,
      string
    >({
      query: (packageId : string) => ({
        url: `/file-courier/package/sent/details/${packageId}`
      }),
    }),
    getFileCourierPackageUrl: builder.query<
      string,
      string
    >({
      query: (packageId : string) => ({
        url: `/file-courier/package/download/${packageId}`
      }),
    }),
    getFileCourierFileUrl: builder.query<
      string,
      string
    >({
      query: (fileId : string) => ({
        url: `/file-courier/file/download/${fileId}`
      }),
    }),
    getRequestDetails: builder.query<AccessRequestDetails, string>({
      query: (query) => ({ url: `/assets/access-requests/${query}` }),
      providesTags: () => [{ type: "Fetch", id: "GET_REQUEST_DETAILS" }],
    }),
    declineAccessRequest: builder.mutation<void, DeclineAssetAccess>({
      query: (declineRequest: DeclineAssetAccess) => ({
        url: `/assets/access-requests/decline/${declineRequest.requestId}`,
        method: "PUT",
        body: JSON.stringify({ reason: declineRequest.reason }),
      }),
      invalidatesTags: [{ type: "Fetch", id: "GET_REQUEST_DETAILS" }],
    }),
    approveAccessRequest: builder.mutation<void, string>({
      query: (requestId) => ({
        url: `/assets/access-requests/approve/${requestId}`,
        method: "PUT",
      }),
      invalidatesTags: [{ type: "Fetch", id: "GET_REQUEST_DETAILS" }],
    }),
    getAllEmailTemplates: builder.query<EmailTemplateMetadata[], void>({
      query: () => ({ url: `/admin/email-template/all` }),
    }),
    getEmailTemplateById: builder.query<EmailTemplate, string>({
      query: (id) => ({ url: `/admin/email-template/${id}` }),
      providesTags: () => [{ type: "Fetch", id: "GET_EMAIL_TEMPLATE" }],
    }),
    getEmailTemplatePreviewById: builder.mutation<
      EmailPreviewResponse,
      EmailTemplate
    >({
      query: (emailTemplate: EmailTemplate) => ({
        url: `/admin/email-template/preview/${emailTemplate.id}`,
        method: "POST",
        body: JSON.stringify({
          body: emailTemplate.body,
          bodyEncoding: "base64"
        }),
      }),
    }),
    updateEmailTemplate: builder.mutation<EmailPreviewResponse, EmailTemplate>({
      query: (emailTemplate: EmailTemplate) => ({
        url: `/admin/email-template/${emailTemplate.id}`,
        method: "PUT",
        body: JSON.stringify({
          name: emailTemplate.name,
          subject: emailTemplate.subject,
          body: emailTemplate.body,
          bodyEncoding: "base64"
        }),
      }),
      invalidatesTags: [{ type: "Fetch", id: "GET_EMAIL_TEMPLATE" }],
    }),
    getRekognition: builder.query<RekognitionResponse[], string[]>({
      query: (ids: string[]) => {
        const idString = ids.reduce(
          (prev: string, curr: string, index: number) =>
            prev +
            "fileversionids=" +
            curr +
            (index !== ids.length - 1 ? "&" : ""),
          ""
        );
        return { url: `/rekognition?${idString}` };
      },
    }),
    getRetailGovernancePage: builder.query<RetailGovernancePage, void>({
      query: () => ({ url: `/cms/retail-governance-process-overview` }),
    }),
    getRetailSupportPage: builder.query<RetailSupportPage, void>({
      query: () => ({ url: `/cms/retail-support-faqs` }),
    }),
    getHome: builder.query<HomePage, void>({
      query: () => ({ url: `/cms/home` }),
    }),
    getTemplates: builder.query<TemplatesPage, void>({
      query: () => ({ url: `/cms/templates` }),
    }),
    getContactUs: builder.query<ContactUsProps, void>({
      query: () => ({ url: `/cms/contact-us` }),
    }),
    getTraining: builder.query<TrainingOverviewPageProps, void>({
      query: () => ({ url: `/cms/training-overview` }),
    }),
    getTrainingVideos: builder.query<TrainingVideosPageProps, void>({
      query: () => ({ url: `/cms/training-videos` }),
    }),
    getCompliance: builder.query<ComplianceOverviewPageProps, void>({
      query: () => ({ url: `/cms/compliance-overview` }),
    }),
    getComplianceProcess: builder.query<ComplianceProcessPageProps, void>({
      query: () => ({ url: `/cms/compliance-process` }),
    }),
    getComplianceScoring: builder.query<ComplianceScoringPageProps, void>({
      query: () => ({ url: `/cms/compliance-scoring` }),
    }),
    getComplianceResources: builder.query<ComplianceResourcePageProps, void>({
      query: () => ({ url: `/cms/compliance-resources` }),
    }),
    getComplianceFaqs: builder.query<ComplianceFaqsPageProps, void>({
      query: () => ({ url: `/cms/compliance-faqs` }),
    }),
    getBrandUpdates: builder.query<BrandUpdatePage, void>({
      query: () => ({ url: `/cms/brand-updates` }),
    }),
    getRetail: builder.query<RetailPageProps, void>({
      query: () => ({ url: `/cms/retail-overview` }),
    }),
    getRetailDesignGuide: builder.query<RetailDesignGuidePageProps, void>({
      query: () => ({ url: `/cms/retail-branded-environments-design-guide` }),
    }),
    getRetailDesignAdvisories: builder.query<
      RetailDesignAdvisoriesPageProps,
      void
    >({
      query: () => ({
        url: `/cms/retail-branded-environments-design-advisories`,
      }),
    }),
    getRetailTemplates: builder.query<RetailTemplatesPageProps, void>({
      query: () => ({ url: `/cms/retail-templates` }),
    }),
    getRetailGovernanceReports: builder.query<
      RetailGovernanceReportsPageProps,
      void
    >({
      query: () => ({ url: `/cms/retail-governance-reports` }),
    }),
    getRetailContactUs: builder.query<RetailContactUsPageProps, void>({
      query: () => ({ url: `/cms/retail-support-contact-us` }),
    }),
    getRetailTools: builder.query<RetailToolsPageProps, void>({
      query: () => ({ url: `/cms/retail-tools` }),
    }),
    getDistributionList: builder.query<DistributionList[], void>({
      query: () => ({ url: `/distribution-lists?category=contact-us` }),
    }),
    getIsPackagingManagerAdmin: builder.query<boolean, void>({
      query: () => ({ url: `/workflow/packaging-manager/permissions/admin` }),
    }),
    getHasPackagingManagerAccess: builder.query<boolean, void>({
      query: () => ({ url: `/workflow/packaging-manager/permissions/access` }),
    }),
    getAllProjects: builder.query<PackagingManagerProjectSearchResults, ProjectsSearchForm>({
      query: (projectsSearchForm: ProjectsSearchForm) => {
      let params = new URLSearchParams();
      const values = {
        page: projectsSearchForm.page,
        pagesize: projectsSearchForm.pageSize,
        ...(projectsSearchForm.q ? { q: projectsSearchForm.q } : {}),
  //       ...(categoryId ? { andfilters: categoryId } : {}),
        sortOn: projectsSearchForm.sortOn,
        sortOrder: projectsSearchForm.sortOrder,
        ...(projectsSearchForm.q ? { query: projectsSearchForm.q } : {}),
      };
      Object.entries(values)
        .filter(
          ([key, value]: [string, any]) =>
            value !== null && value !== undefined
        )
        .map(([key, value]: [string, any]) => {params.append(key, value);
        });
      projectsSearchForm.statusFilters.map((status: string) => {params.append("statusFilters", status);
      });
      projectsSearchForm.groupFilters.map((group: string) => {params.append("groupFilters", group);
      });
      projectsSearchForm.projectTypeFilters.map((project: string) => {params.append("projectTypeFilters", project);
      });
      return { url: `/packaging-manager/project/all`, params: params };
      },
    }),
    getProjectTypes: builder.query<Array<ProjectTypeProps>, void>({
      query: () => ({ url: `/packaging-manager/project/blueprints` }),
    }),
    getProjectInstanceCurrentVersion: builder.query<ProjectInstanceResponse, string>({
      query: (projectInstanceId: string) => ({ url: `/packaging-manager/project/${projectInstanceId}` }),
    }),
    getProjectInstanceCurrentVersionFolders: builder.query<ProjectFoldersResponse, ProjectFolderRequestForm>({
      query: (projectFolderRequestForm: ProjectFolderRequestForm) => ({
        url: `/packaging-manager/project/folders/${projectFolderRequestForm.projectInstanceId}`,
        params: {
          projectInstanceId: projectFolderRequestForm.projectInstanceId,
          sortOn: projectFolderRequestForm.sortOn,
          sortOrder: projectFolderRequestForm.sortOrder,
          q: projectFolderRequestForm.query
        }
      }),
    }),
    updateProjectInstance: builder.mutation<ProjectInstanceResponse, ProjectUpdateInstanceForm>({
      query: (projectUpdateInstanceForm: ProjectUpdateInstanceForm) => ({
        url: `/packaging-manager/project/update/properties/${projectUpdateInstanceForm.projectInstanceId}`,
        method: "PUT",
        body: JSON.stringify({
          ProjectInstanceId: projectUpdateInstanceForm.projectInstanceId,
          Name: projectUpdateInstanceForm.name,
          Status: projectUpdateInstanceForm.status,
        }),
      }),
    }),
    updateProjectFolders: builder.mutation<Array<ProjectFolderProps>, ProjectFoldersUpdateRequest>({
      query: (projectFoldersUpdateRequest: ProjectFoldersUpdateRequest) => ({
        url: `/packaging-manager/project/update/folders/${projectFoldersUpdateRequest.projectInstanceId}`,
        method: "PUT",
        body: JSON.stringify(projectFoldersUpdateRequest.folders),
      }),
    }),
    updateProjectTeams: builder.mutation<Array<ProjectTeamProps>, ProjectTeamsUpdateRequest>({
      query: (projectTeamsUpdateRequest: ProjectTeamsUpdateRequest) => ({
        url: `/packaging-manager/project/update/teams/${projectTeamsUpdateRequest.projectInstanceId}`,
        method: "PUT",
        body: JSON.stringify(projectTeamsUpdateRequest.teams),
      }),
    }),
    deleteProjectTeam: builder.mutation<ProjectTeamProps, ProjectTeamDeleteRequest>({
      query: (projectTeamDeleteRequest: ProjectTeamDeleteRequest) => ({
        url: `/packaging-manager/project/delete/team/${projectTeamDeleteRequest.projectInstanceId}`,
        method: "DELETE",
        body: JSON.stringify(projectTeamDeleteRequest.team),
      }),
    }),
    createProjectTeam: builder.mutation<string, ProjectTeamCreateRequest>({
      query: (projectTeamCreateRequest: ProjectTeamCreateRequest) => ({
        url: `/packaging-manager/project/update/teams/${projectTeamCreateRequest.projectInstanceId}`,
        method: "POST",
        body: JSON.stringify({
          ProjectInstanceId: projectTeamCreateRequest.projectInstanceId,
          Name: projectTeamCreateRequest.name,
          Groups: projectTeamCreateRequest.groups
        }),
      }),
    }),
    getProjectGroupMembers: builder.query<Array<GroupMemberProps>, ProjectGroupMembersRequest>({
      query: (projectGroupMembersRequest: ProjectGroupMembersRequest) => ({
        url: `/packaging-manager/project/team/group-members/${projectGroupMembersRequest.projectInstanceId}/${projectGroupMembersRequest.groupId}`
      }),
    }),
    createNewProjectInstance: builder.mutation<string, ProjectCreateInstanceForm>({
      query: (projectCreateInstanceForm: ProjectCreateInstanceForm) => ({
        url: `/packaging-manager/project/create/${projectCreateInstanceForm.projectBlueprintId}`,
        method: "POST",
        body: JSON.stringify({
          ProjectBlueprintId: projectCreateInstanceForm.projectBlueprintId,
          Name: projectCreateInstanceForm.name,
        }),
      }),
    }),
    getProjectTasks: builder.query<Array<ProjectTask>, GetProjectTasksForm>({
      query: (getProjectTasksForm: GetProjectTasksForm) => ({ url: `/packaging-manager/project-task/tasks/${getProjectTasksForm.projectInstanceId}/${getProjectTasksForm.taskFilter}` }),
    }),
    getTaskInstanceCurrentVersion: builder.query<ProjectTaskDetailsResponse, GetProjectTaskForm>({
      query: (getProjectTaskForm: GetProjectTaskForm) => ({ url: `/packaging-manager/project-task/task/${getProjectTaskForm.projectInstanceId}/${getProjectTaskForm.taskInstanceId}` }),
    }),
    updateProjectTask: builder.mutation<any, ProjectTaskUpdateRequest>({
      query: (projectTaskUpdateRequest: ProjectTaskUpdateRequest) => ({
        url: `/packaging-manager/project-task/update/task/${projectTaskUpdateRequest.projectInstanceId}`,
        method: "PUT",
        body: JSON.stringify({
          taskInstanceId: projectTaskUpdateRequest.task.taskInstanceId,
          taskInstanceStepId: projectTaskUpdateRequest.task.taskInstanceStepId,
          stepType: projectTaskUpdateRequest.task.stepType,
          status: projectTaskUpdateRequest.task.status,
          name: projectTaskUpdateRequest.task.name,
          files: projectTaskUpdateRequest.task.files?.map((file) => file),
          description: projectTaskUpdateRequest.task.description,
          assignedToTeams: projectTaskUpdateRequest.task.assignedToTeams?.map((team) => team),
          dueDate: projectTaskUpdateRequest.task.dueDate,
          checklist: projectTaskUpdateRequest.task.checklist.map((checklistStep) => checklistStep)
        }),
      }),
    }),
    createProjectTask: builder.mutation<string, TaskCreateRequest>({
      query: (taskCreateRequest: TaskCreateRequest) => ({
        url: `/packaging-manager/project-task/add/task/${taskCreateRequest.projectInstanceId}`,
        method: "POST",
        body: JSON.stringify({
          type: taskCreateRequest.task.type,
          name: taskCreateRequest.task.name,
          description: taskCreateRequest.task.description,
          assignedToTeams: taskCreateRequest.task.assignedToTeams?.map((group) => group),
          dueDate: taskCreateRequest.task.dueDate,
          formBlueprintId: taskCreateRequest.task.formBlueprintId,
          files: taskCreateRequest.task.files?.map((file) => file),
          checklistLabels: taskCreateRequest.task.checklistLabels.map((checklistStep) => checklistStep)
        }),
      }),
    }),
    getTaskFormTypes: builder.query<Array<TaskFormType>, void>({
      query: () => ({ url: `/packaging-manager/form/blueprints/` }),
    }),
    deleteTask: builder.mutation<void, DeleteTaskRequest>({
      query: (deleteTaskRequest: DeleteTaskRequest) => ({
        url: `/packaging-manager/project-task/delete/task/${deleteTaskRequest.projectInstanceId}/${deleteTaskRequest.taskInstanceId}`,
        method: "DELETE",
      })
    }),
    deleteTaskFile: builder.mutation<void, DeleteTaskFileRequest>({
      query: (deleteTaskFileRequest: DeleteTaskFileRequest) => ({
        url: `/packaging-manager/file/delete/task/${deleteTaskFileRequest.projectInstanceId}/${deleteTaskFileRequest.taskInstanceId}/${deleteTaskFileRequest.parentFileId}`,
        method: "DELETE",
      })
    }),
    deleteProjectFile: builder.mutation<void, DeleteProjectFileRequest>({
      query: (deleteProjectFileRequest: DeleteProjectFileRequest) => ({
        url: `/packaging-manager/file/delete/project/${deleteProjectFileRequest.projectInstanceId}/${deleteProjectFileRequest.parentFileId}`,
        method: "DELETE",
      })
    }),
    deleteFormFile: builder.mutation<void, DeleteFormFileRequest>({
      query: (deleteFormFileRequest: DeleteFormFileRequest) => ({
        url: `/packaging-manager/file/delete/form/${deleteFormFileRequest.projectInstanceId}/${deleteFormFileRequest.formInstanceId}/${deleteFormFileRequest.parentFileId}`,
        method: "DELETE",
      })
    }),
    getAwsWorkflowPolicy: builder.query<AwsPolicyProps, void>({
      query: () => ({ url: "/Aws/workflow" }),
    }),
    uploadPackagingManagerFile: builder.mutation<string, FileCreateRequest>({
      query: (fileCreateRequest: FileCreateRequest) => ({
        url: `/packaging-manager/file/add/${fileCreateRequest.attachedToType}${fileCreateRequest.projectInstanceId ? `/${fileCreateRequest.projectInstanceId}` : ""}/${fileCreateRequest.file.instanceId}`,
        method: "POST",
        body: JSON.stringify({
          FileId: fileCreateRequest.file.fileId,
          InstanceId: fileCreateRequest.file.instanceId,
          Name: fileCreateRequest.file.name,
          Extension: fileCreateRequest.file.extension,
          MimeType: fileCreateRequest.file.mimeType,
          SizeInBytes: fileCreateRequest.file.sizeInBytes,
        }),
      }),
    }),
    uploadMultiplePackagingManagerFiles: builder.mutation<Array<FormFileUpdateCreateResponse>, MultipleFileCreateRequest>({
      query: (multipleFileCreateRequest: MultipleFileCreateRequest) => ({
        url: `/packaging-manager/file/add-multiple/form/${multipleFileCreateRequest.projectInstanceId}/${multipleFileCreateRequest.formInstanceId}`,
        method: "POST",
        body: JSON.stringify({
          InstanceId: multipleFileCreateRequest.formInstanceId,
          Files: multipleFileCreateRequest.files.map((file) => file)
        }),
      }),
    }),
    updateMultiplePackagingManagerFiles: builder.mutation<Array<FormFileUpdateCreateResponse>, MultipleFileUpdateRequest>({
      query: (multipleFileUpdateRequest: MultipleFileUpdateRequest) => ({
        url: `/packaging-manager/file/update-multiple/form/${multipleFileUpdateRequest.formInstanceId}`,
        method: "PUT",
        body: JSON.stringify({
          InstanceId: multipleFileUpdateRequest.formInstanceId,
          Files: multipleFileUpdateRequest.files.map((file) => file)
        }),
      }),
    }),
    updatePackagingManagerFile: builder.mutation<string, FileCreateRequest>({
      query: (fileCreateRequest: FileCreateRequest) => ({
        url: `/packaging-manager/file/update/${fileCreateRequest.attachedToType}/${fileCreateRequest.file.instanceId}`,
        method: "PUT",
        body: JSON.stringify({
          FileId: fileCreateRequest.file.fileId,
          InstanceId: fileCreateRequest.file.instanceId,
          ParentFileId: fileCreateRequest.file.parentFileId,
          Name: fileCreateRequest.file.name,
          Extension: fileCreateRequest.file.extension,
          MimeType: fileCreateRequest.file.mimeType,
          SizeInBytes: fileCreateRequest.file.sizeInBytes,
          FolderId: fileCreateRequest.file.folderId ? fileCreateRequest.file.folderId : null
        }),
      }),
    }),
    updatePackagingManagerFileFolder: builder.mutation<string, FileUpdateFolderRequest>({
      query: (fileUpdateFolderRequest: FileUpdateFolderRequest) => ({
        url: `/packaging-manager/file/update/${fileUpdateFolderRequest.attachedToType}/folder/${fileUpdateFolderRequest.instanceId}`,
        method: "PUT",
        body: JSON.stringify({
          ProjectInstanceId: fileUpdateFolderRequest.projectInstanceId,
          InstanceId: fileUpdateFolderRequest.instanceId,
          ParentFileId: fileUpdateFolderRequest.file.parentFileId,
          FolderId: fileUpdateFolderRequest.file.folderId ? fileUpdateFolderRequest.file.folderId : null
        }),
      }),
    }),
    deleteFileVersion: builder.mutation<void, DeleteFileVersionRequest>({
      query: (deleteFileVersionRequest: DeleteFileVersionRequest) => ({
        url: `/packaging-manager/file/version/delete/${deleteFileVersionRequest.attachedToType}/${deleteFileVersionRequest.instanceId}/${deleteFileVersionRequest.parentFileId}/${deleteFileVersionRequest.fileId}`,
        method: "DELETE",
      })
    }),
    getFileVersions: builder.query<Array<FileVersionResult>, FileVersionsRequest>({
      query: (fileVersionsRequest: FileVersionsRequest) => ({ url: `/packaging-manager/file/version/details/${fileVersionsRequest.projectInstanceId}/${fileVersionsRequest.parentFileId}` }),
    }),
    downloadPackagingManagerFileVersion: builder.query<string, string>({
      query: (fileId: string) => ({ url: `/packaging-manager/file/download/version/${fileId}` }),
    }),
    getProjectBriefs: builder.query<Array<ProjectBriefSearchResponse>, string>({
      query: (projectInstanceId: string) => ({ url: `/packaging-manager/project-form/${projectInstanceId}` }),
    }),
    getFormInstanceAndBlueprint: builder.query<PackagingManagerFormResponse, FormDetailsRequest>({
      query: (formDetailsRequest: FormDetailsRequest) => ({ url: `/packaging-manager/project-form/${formDetailsRequest.projectInstanceId}/${formDetailsRequest.projectInstanceFormId}` }),
    }),
    updateFormInstance: builder.mutation<FormInstanceVersion, UpdateFormRequest>({
      query: (updateFormRequest: UpdateFormRequest) => ({
        url: `/packaging-manager/project-form/values/${updateFormRequest.projectInstanceId}/${updateFormRequest.projectInstanceFormId}`,
        method: "PUT",
        body: JSON.stringify({
          Id: updateFormRequest.formInstanceVersion.id,
          Version: updateFormRequest.formInstanceVersion.version,
          IsComplete: updateFormRequest.formInstanceVersion.isComplete,
          CreatedOn: updateFormRequest.formInstanceVersion.createdOn,
          ModifiedOn: updateFormRequest.formInstanceVersion.modifiedOn,
          CompletedOn: updateFormRequest.formInstanceVersion.completedOn,
          Pages: updateFormRequest.formInstanceVersion.pages.map((page) => page)
        }),
      }),
    }),
    submitFormInstance: builder.mutation<FormInstanceVersion, SubmitFormRequest>({
      query: (submitFormRequest: SubmitFormRequest) => ({
        url: `/packaging-manager/project-form/submit/${submitFormRequest.projectInstanceId}/${submitFormRequest.projectInstanceFormId}`,
        method: "POST",
        body: submitFormRequest.pdfFile,
        headers: {
          accepts: "multipart/form-data",
          "Content-type": "multipart/form-data",
        }
      }),
    }),
    getProjectFiles: builder.query<PackagingManagerFileSearchResults, FilesSearchForm>({
      query: (filesSearchForm: FilesSearchForm) => {
        let params = new URLSearchParams();
        const values = {
          ...(filesSearchForm.q ? { q: filesSearchForm.q } : {}),
          filter: filesSearchForm.filter,
          page: filesSearchForm.page,
          pagesize: filesSearchForm.pagesize
        };
        Object.entries(values)
        .filter(
          ([key, value]: [string, any]) =>
            value !== null && value !== undefined
        )
        .map(([key, value]: [string, any]) => {params.append(key, value);
        });
        return { url: `/packaging-manager/project-file/files/${filesSearchForm.projectInstanceId}`, params: params };
      },
    }),
    exportPackagingManagerReports: builder.query<void, void>({
      query: () => ({
        url: `/packaging-manager/project-report/projects/export` }),
    }),
    getProjectMessages: builder.query<PackagingManagerMessageSearchResults, MessagesSearchForm>({
      query: (messagesSearchForm: MessagesSearchForm) => {
        let params = new URLSearchParams();
        const values = {
          ...(messagesSearchForm.q ? { q: messagesSearchForm.q } : {}),
          pageSize: messagesSearchForm.pageSize,
          page: messagesSearchForm.page,
          sortOn: messagesSearchForm.sortOn,
          sortOrder: messagesSearchForm.sortOrder
        };
        Object.entries(values)
        .filter(
          ([key, value]: [string, any]) =>
            value !== null && value !== undefined
        )
        .map(([key, value]: [string, any]) => {params.append(key, value);
        });
        return { url: `/packaging-manager/project-message/messages/${messagesSearchForm.projectInstanceId}`, params: params };
      },
    }),

    createProjectMessage: builder.mutation<string, ProjectMessagesCreateRequest>({
      query: (messageCreateRequest: ProjectMessagesCreateRequest) => ({
        url: `/packaging-manager/message/add/project/${messageCreateRequest.projectInstanceId}`,
        method: "POST",
        body: JSON.stringify({
          instanceId: messageCreateRequest.projectInstanceId,
          body: messageCreateRequest.body,
          sendToTeamIds: messageCreateRequest.sendToTeamIds
        }),
      }),
    }),

    deleteProjectMessage: builder.mutation<string, ProjectMessageDeleteRequest>({
      query: (deleteProjectMessageRequest: ProjectMessageDeleteRequest) => ({
        url: `/packaging-manager/message/delete/project/${deleteProjectMessageRequest.projectInstanceId}/${deleteProjectMessageRequest.messageId}`,
        method: "DELETE",
      })
    }),

    updateProjectMessage: builder.mutation<string, ProjectMessageUpdateRequest>({
      query: (messageUpdateRequest: ProjectMessageUpdateRequest) => ({
        url: `/packaging-manager/message/update/project/${messageUpdateRequest.projectInstanceId}`,
        method: "PUT",
        body: JSON.stringify({
          messageId: messageUpdateRequest.messageId,
          instanceId: messageUpdateRequest.projectInstanceId,
          body: messageUpdateRequest.body,
          sendToTeamIds: messageUpdateRequest.sendToTeamIds
        }),
      }),
    }),

    getWorkflowPermissions: builder.query<WorkflowPermissionDto[], string>({
      query: (clientCode: string) => ({ url: `/workflow/groups/permissions/${clientCode}` }),
    }),

    getGroupTypes: builder.query<GroupType[], string>({
      query: (clientCode: string) => ({ url: `/workflow/groups/group-types/${clientCode}` }),
    }),

    updateWorkflowPermissions: builder.mutation<string, EditWorkflowPermissionsRequest>({
      query: (editPermissionsRequest: EditWorkflowPermissionsRequest) => ({
        url: `/workflow/groups/update/${editPermissionsRequest.groupId}`,
        method: "PUT",
        body: JSON.stringify({
          id: editPermissionsRequest.id,
          groupTypeId: editPermissionsRequest.groupTypeId,
          workflowPermissionId: editPermissionsRequest.workflowPermissionId
        }),
      }),
    }),

    deleteWorkflowPermissions: builder.mutation<string, string>({
      query: (groupId: string) => ({
        url: `/workflow/groups/delete/${groupId}`,
        method: "PUT"
      }),
    }),

    getDefaultTasks: builder.query<string[], void>({
      query: () => ({ url: '/packaging-manager/project-task/default-task/groups' }),
    }),
    
    createDefaultTasks: builder.mutation<string, CreateDefaultTasksRequest>({
      query: (createTasksRequests: CreateDefaultTasksRequest) => ({
        url: `/packaging-manager/project-task/default-task/add/group/${createTasksRequests.projectInstanceId}/${createTasksRequests.taskGroupName}`,
        method: "POST"
      }),
    }),
    createUnifiedIndex: builder.mutation<void, void>({
      query: () => ({
        url: `/index/management/unified/create`,
        method: "POST"
      }),
    }),
    unifiedReindex: builder.mutation<void, string>({
      query: (searchType: string) => ({
        url: `/index/management/unified/reindex`,
        method: "POST",
        body: JSON.stringify({searchType: searchType})
      }),
    }),

  }),
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useLazyGetAwsPolicyQuery,
  useLazyGetAwsVideoPolicyQuery,
  useGetCountriesQuery,
  useGetEmploymentTitlesQuery,
  useGetGlobalQuery,
  useLazyGetFavoriteItemsQuery,
  useGetFavoriteFoldersQuery,
  useLazyGetFavoriteFoldersQuery,
  useLazyGetFavoritesPageFoldersQuery,
  useGetFiltersQuery,
  useGetCategoriesQuery,
  useNewCategoryMutation,
  useNewSubCategoryMutation,
  useNewTagMutation,
  useEditCategoryMutation,
  useEditSubCategoryMutation,
  useEditTagMutation,
  useLazyGetSubCategoriesQuery,
  useLazyGetTagsQuery,
  useAddFavoriteAssetMutation,
  useRemoveFavoriteAssetMutation,
  useActivateAssetMutation,
  useExpireAssetMutation,
  useTrackFeedbackMutation,
  useAcceptUsageRightsMutation,
  useGetFeedbackOptionsQuery,
  useLazyGetAssetDownloadUrlQuery,
  useLazyGetFileDownloadUrlQuery,
  useLazyGetFileGroupDownloadUrlQuery,
  useLazyGetChannelsUsesQuery,
  useAccessRequestsMutation,
  useShareAssetsMutation,
  useLazyGetSuggestedUsersQuery,
  useLazyGetRelatedAssetsQuery,
  useGetGroupsQuery,
  useLazyAdminGetGroupsQuery,
  useAdminGetGroupsQuery,
  useAdminCreateGroupMutation,
  useTrackingMutation,
  useLazyAdminGetUsersQuery,
  useAdminGetUserQuery,
  useLazyAdminGetUserQuery,
  useDeleteUserMutation,
  useApproveUserMutation,
  useLazyGetThumbnailsQuery,
  useEditAssetMutation,
  useApproveFileMutation,
  useAdminUpdateGroupMutation,
  useGetAccountTypesQuery,
  useGetRolesQuery,
  useGetPermissionsQuery,
  useLazySearchAssetsQuery,
  useLazySearchGuidelinesQuery,
  useLazySearchTrainingQuery,
  useLazySearchComplianceQuery,
  useLazyGetSuggestedSearchQuery,
  useLazyGetRecentSearchesQuery,
  useDeleteRecentSearchMutation,
  useLazyGetRelatedSearchesQuery,
  useSaveSearchMutation,
  useRequestAssetMutation,
  useUpdateUserMutation,
  useLazyAdminGetGroupQuery,
  useAdminGetGroupQuery,
  useLazyGetUploadedAssetsQuery,
  useLazyGetDownloadedAssetsQuery,
  useLazyGetGroupMembersQuery,
  useLazyGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useGetFunctionalAreasQuery,
  useGetChannelsQuery,
  useLazyGetAssetsQuery,
  useDeleteFavoriteFolderMutation,
  useSendFeedbackMutation,
  useSendContactMutation,
  useGetSavedSearchesQuery,
  useLazyGetSavedSearchesQuery,
  useLazyGetSavedSearchQuery,
  useShareSavedSearchMutation,
  useDeleteSavedSearchMutation,
  useRenameSavedSearchMutation,
  useLazyGetAwsFileCourierPolicyQuery,
  useSendFileCourierPackageMutation,
  useDeleteFileCourierFileMutation,
  useLazyGetFileCourierSentPackagesQuery,
  useLazyGetFileCourierReceivedPackagesQuery,
  useDeleteFileCourierPackageMutation,
  useExtendFileCourierPackageMutation,
  useLazyGetSentFileCourierPackageDetailsQuery,
  useLazyGetRequestorAccessRequestsQuery,
  useLazyGetApproverAccessRequestsQuery,
  useLazyGetRequestDetailsQuery,
  useDeclineAccessRequestMutation,
  useApproveAccessRequestMutation,
  useCreateAssetMutation,
  useLazyGetAllEmailTemplatesQuery,
  useLazyGetEmailTemplateByIdQuery,
  useGetEmailTemplatePreviewByIdMutation,
  useUpdateEmailTemplateMutation,
  useLazyGetRekognitionQuery,
  useLazyAdminAssetSearchQuery,
  useLazyGetRetailGovernancePageQuery,
  useLazyGetRetailSupportPageQuery,
  useGetAssetsDashboardQuery,
  useGetNewAssetsQuery,
  useGetRecommendedAssetsQuery,
  useGetSimilarAssetsQuery,
  useLazyGetAssignedAssetsQuery,
  useGetHomeQuery,
  useGetTemplatesQuery,
  useGetContactUsQuery,
  useGetTrainingQuery,
  useGetTrainingVideosQuery,
  useGetComplianceQuery,
  useGetComplianceProcessQuery,
  useGetComplianceScoringQuery,
  useGetComplianceResourcesQuery,
  useGetComplianceFaqsQuery,
  useGetBrandUpdatesQuery,
  useGetRetailQuery,
  useLazyGetRetailQuery,
  useGetRetailDesignGuideQuery,
  useGetRetailDesignAdvisoriesQuery,
  useGetRetailTemplatesQuery,
  useGetRetailGovernanceReportsQuery,
  useGetRetailContactUsQuery,
  useGetRetailToolsQuery,
  useGetRelatedAssetsSpecificQuery,
  useGetSimilarAssetsSpecificQuery,
  useGetDistributionListQuery,
  useLazyGetAssetDetailsQuery,
  useLazyGetDownloadAccessRequestsLinkQuery,
  useGetProjectTypesQuery,
  useCreateNewProjectInstanceMutation,
  useUpdateProjectInstanceMutation,
  useLazyGetProjectInstanceCurrentVersionQuery,
  useLazyGetTaskInstanceCurrentVersionQuery,
  useUpdateProjectTeamsMutation,
  useLazyGetPackagingManagerGroupsQuery,
  useCreateProjectTeamMutation,
  useLazyGetProjectGroupMembersQuery,
  useLazyGetProjectTasksQuery,
  useUpdateProjectTaskMutation,
  useGetTaskFormTypesQuery,
  useCreateProjectTaskMutation,
  useDeleteTaskMutation,
  useLazyGetAllProjectsQuery,
  useLazyGetAwsWorkflowPolicyQuery,
  useUploadPackagingManagerFileMutation,
  useLazyGetFileVersionsQuery,
  useDeleteTaskFileMutation,
  useDeleteProjectFileMutation,
  useDeleteFormFileMutation,
  useUpdatePackagingManagerFileMutation,
  useDeleteFileVersionMutation,
  useLazyDownloadPackagingManagerFileVersionQuery,
  useLazyGetFormInstanceAndBlueprintQuery,
  useUpdateFormInstanceMutation,
  useSubmitFormInstanceMutation,
  useLazyGetProjectBriefsQuery,
  useLazyGetProjectFilesQuery,
  useLazyGetProjectMessagesQuery,
  useCreateProjectMessageMutation,
  useDeleteProjectMessageMutation,
  useUpdateProjectMessageMutation,
  useLazyExportPackagingManagerReportsQuery,
  useLazyGetWorkflowPermissionsQuery,
  useLazyGetGroupTypesQuery,
  useUpdateWorkflowPermissionsMutation,
  useDeleteWorkflowPermissionsMutation,
  useLazyGetIsPackagingManagerAdminQuery,
  useLazyGetDefaultTasksQuery,
  useCreateDefaultTasksMutation,
  useLazyGetHasPackagingManagerAccessQuery,
  useUploadMultiplePackagingManagerFilesMutation,
  useUpdatePackagingManagerFileFolderMutation,
  useUpdateProjectFoldersMutation,
  useLazyGetProjectInstanceCurrentVersionFoldersQuery,
  useUpdateMultiplePackagingManagerFilesMutation,
  useDeleteProjectTeamMutation,
  useAddLogInToHistoryMutation,
  useLazyGetFileCourierPackageUrlQuery,
  useLazyGetFileCourierFileUrlQuery,
  useCreateUnifiedIndexMutation,
  useUnifiedReindexMutation
} = apiSlice;
